import { Link, useLocation, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/App";
import { useLanguage } from "@/lib/i18n";
import { LogOut, Menu, Shield, LayoutDashboard, Settings, Bell, X, Gift, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { tokenStore } from "@/lib/token";
import Sidebar from "@/components/Sidebar";
import AuthModal from "@/components/AuthModal";
import UserAvatar from "@/components/UserAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const NAV_LINKS = [
  { to: "/shop", label: "Boutique" },
  { to: "/comparer", label: "Comparer" },
  { to: "/ai", label: "Vanta AI" },
  { to: "/support", label: "Support" },
  { to: "/status", label: "Statut" },
];

export const Navbar = ({ onAuthClick }) => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const { user, logout, profile } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [removingIds, setRemovingIds] = useState(new Set());
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [offerToAccept, setOfferToAccept] = useState(null); // { notifId, offerId, description }
  const [offerToken, setOfferToken] = useState("");
  const [offerBotName, setOfferBotName] = useState("");
  const [offerLoading, setOfferLoading] = useState(false);
  const handleAuthClick = onAuthClick || (() => setShowAuth(true));

  useEffect(() => {
    let active = true;
    const loadAnnouncements = async () => {
      try {
        const response = await axios.get(`${API}/info`);
        let dismissed = [];
        const token = tokenStore.get();
        if (user && token) {
          try {
            dismissed = (await axios.get(`${API}/announcements/dismissed`, { headers: { Authorization: `Bearer ${token}` } })).data || [];
          } catch { /* sans la liste, on affiche tout */ }
        }
        if (!active) return;
        const announcementNotifications = (response.data || []).filter((announcement) => !dismissed.includes(announcement.id)).map((announcement) => ({
          id: `announcement-${announcement.id}`,
          announcementId: announcement.id,
          message: announcement.title,
          content: announcement.content || "",
          time: new Date(announcement.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }),
        }));
        setNotifications((current) => [
          ...current.filter((notification) => !notification.announcementId),
          ...announcementNotifications,
        ]);
      } catch { /* notifications are optional */ }
    };
    const loadPersonalNotifications = async () => {
      const token = tokenStore.get();
      if (!user || !token) return;
      try {
        const response = await axios.get(`${API}/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!active) return;
        const personal = (response.data || []).map((notif) => ({
          id: notif.id,
          message: notif.title,
          content: notif.message,
          time: new Date(notif.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }),
          offerId: notif.type === "bot_offer" ? notif.offer_id : undefined,
          botOfferMessage: notif.type === "bot_offer" ? notif.message : undefined,
          offerDownloadable: notif.type === "bot_offer" ? notif.downloadable === true : undefined,
        }));
        // On garde seulement les annonces déjà chargées : les notifications
        // personnelles sont remplacées (sinon elles se dupliquent à chaque recharge).
        setNotifications((current) => [
          ...personal,
          ...current.filter((notification) => notification.announcementId),
        ]);
      } catch { /* notifications are optional */ }
    };
    loadAnnouncements();
    loadPersonalNotifications();
    const refreshTimer = window.setInterval(() => { loadAnnouncements(); loadPersonalNotifications(); }, 60000);
    return () => { active = false; window.clearInterval(refreshTimer); };
  }, [user]);

  const openAcceptOffer = (notif) => {
    setNotificationsOpen(false);
    setOfferToken("");
    setOfferBotName("");
    setOfferToAccept({ notifId: notif.id, offerId: notif.offerId, description: notif.botOfferMessage || notif.message, downloadable: notif.offerDownloadable === true });
  };

  // Un bot Discord offert est relié au compte Discord du client : il faut l'avoir connecté.
  const offerNeedsDiscord = !!offerToAccept && !offerToAccept.downloadable && !user?.discord_linked;

  const connectDiscordForOffer = () => {
    const token = tokenStore.get();
    const origin = encodeURIComponent(window.location.origin);
    window.location.href = `${API}/auth/discord/link/start?origin=${origin}&token=${token}`;
  };

  const confirmAcceptOffer = async () => {
    if (!offerToAccept) return;
    // A script (downloadable product) needs no bot name nor Discord token.
    if (!offerToAccept.downloadable) {
      if (offerNeedsDiscord) { toast.error("Connecte d'abord ton compte Discord"); return; }
      if (!offerBotName.trim()) { toast.error("Nom du bot requis"); return; }
      if (!offerToken.trim()) { toast.error("Token Discord requis"); return; }
    }
    setOfferLoading(true);
    try {
      const token = tokenStore.get();
      await axios.post(`${API}/bot-offers/${offerToAccept.offerId}/accept`, {
        bot_name: offerBotName, discord_token: offerToken,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((current) => current.filter((n) => n.id !== offerToAccept.notifId));
      toast.success(offerToAccept.downloadable
        ? "Produit ajouté ! Ta clé de licence et le téléchargement sont dans ton dashboard."
        : "Bot activé ! Retrouve-le dans ton dashboard.");
      setOfferToAccept(null);
      setOfferToken("");
      setOfferBotName("");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible d'accepter l'offre");
    } finally {
      setOfferLoading(false);
    }
  };

  const declineOffer = async (notif) => {
    try {
      const token = tokenStore.get();
      await axios.post(`${API}/bot-offers/${notif.offerId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((current) => current.filter((n) => n.id !== notif.id));
      toast.success("Offre refusée");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  // Affichage seulement : c'est le serveur qui décide (email exact du fondateur ou staff).
  const ADMIN_EMAILS = ["tilyesgamer@gmail.com"];
  const ADMIN_DISCORD_ID = "1121136606733152376";

  const isAdmin = user && (
    user.is_admin ||
    user.role === 'admin' ||
    user.role === 'support' ||
    ADMIN_EMAILS.includes(user.email) ||
    user.discord_id === ADMIN_DISCORD_ID
  );

  const handleLogout = () => { logout(); navigate('/'); };

  const removeNotification = (e, notif) => {
    e.stopPropagation();
    const id = notif.id;
    setRemovingIds(prev => new Set([...prev, id]));
    const token = tokenStore.get();
    const headers = { Authorization: `Bearer ${token}` };
    // Enregistré sur le serveur : la notification ne revient pas à l'actualisation.
    if (notif.announcementId) {
      axios.post(`${API}/announcements/${notif.announcementId}/dismiss`, {}, { headers }).catch(() => {});
    } else {
      axios.post(`${API}/notifications/${id}/read`, {}, { headers }).catch(() => {});
    }
    setTimeout(() => {
      setNotifications((current) => current.filter(n => n.id !== id));
      setRemovingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, 300);
  };

  // « Tout effacer » : tout sauf les offres de produit en attente (elles
  // attendent une réponse Accepter / Refuser).
  const clearableNotifications = notifications.filter((n) => !n.offerId);
  const clearAllNotifications = async () => {
    const token = tokenStore.get();
    const announcementIds = clearableNotifications.filter((n) => n.announcementId).map((n) => n.announcementId);
    setNotifications((current) => current.filter((n) => n.offerId));
    try {
      await axios.post(`${API}/notifications/clear`, { announcement_ids: announcementIds }, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      toast.error("Impossible de vider les notifications, réessaie");
    }
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${scrolled ? "border-white/[0.07] bg-[#0a0a0f]/75 backdrop-blur-xl" : "border-transparent bg-[#0a0a0f]/30 backdrop-blur-md"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Hamburger + Logo */}
            <div className="flex items-center gap-3">
              <button data-testid="hamburger-btn" onClick={() => setSidebarOpen(true)} aria-label="Ouvrir le menu" className="p-2 hover:bg-white/[0.06] rounded-lg transition-colors">
                <Menu className="w-5 h-5 text-white/70" />
              </button>
              <Link to="/" className="flex items-center gap-3">
                <img src="/images/logo-navbar.png" alt="VantaPulse" className="w-9 h-9 rounded-full ring-1 ring-white/10" />
                <span className="font-heading font-bold text-lg tracking-tight hidden sm:block">VantaPulse</span>
              </Link>
            </div>

            {/* Centre : liens principaux (ordinateur) */}
            <div className="hidden lg:flex items-center gap-1 rounded-full border border-white/[0.06] bg-white/[0.025] p-1">
              {NAV_LINKS.map((l) => {
                const active = location.pathname === l.to || location.pathname.startsWith(`${l.to}/`);
                return (
                  <Link key={l.to} to={l.to} data-testid={`navbar-link-${l.to.slice(1)}`} className={`rounded-full px-4 py-1.5 text-[13px] font-medium transition-colors ${active ? "bg-white/10 text-white" : "text-white/55 hover:text-white"}`}>
                    {l.label}
                  </Link>
                );
              })}
            </div>

            {/* Right: User */}
            <div className="flex items-center gap-3">
              {user ? (
                <>
                  <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="relative text-white/60 hover:text-white"
                      >
                        <Bell className="w-5 h-5" />
                        {notifications.length > 0 && <span data-testid="notif-dot" className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse border-2 border-[#0a0a0f]"></span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="notification-popover bg-[#12121a] border-white/10 text-white max-w-md w-80 mr-4" align="end" sideOffset={10}>
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold flex items-center gap-2">
                          <Bell className="w-5 h-5 text-blue-400" />
                          {t('nav_notifications')}
                        </h2>
                        <div className="flex items-center gap-1">
                          {clearableNotifications.length > 0 && (
                            <button data-testid="notif-clear-all" onClick={clearAllNotifications} className="text-xs text-white/50 hover:text-white px-2 py-1 rounded hover:bg-white/5 transition-colors">
                              Tout effacer
                            </button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => setNotificationsOpen(false)} className="h-6 w-6">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {notifications.map((notif) => (
                          <div 
                            key={notif.id} 
                            className={`p-3 bg-white/5 rounded-xl border border-white/10 relative group animate-in fade-in slide-in-from-top-2 duration-300 hover:border-blue-400/30 hover:bg-blue-500/5 transition-colors ${removingIds.has(notif.id) ? 'animate-out fade-out slide-out-to-right-2 duration-300' : ''}`}
                          >
                            <button
                              onClick={(e) => removeNotification(e, notif)}
                              data-testid="notif-remove"
                              aria-label="Effacer"
                              className="absolute top-2 right-2 opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-white/40 hover:text-white p-1 hover:bg-white/10 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <p className="text-sm text-white/80 flex items-center gap-1.5 pr-5">
                              {notif.offerId && <Gift className="w-3.5 h-3.5 text-green-400 shrink-0" />}
                              {notif.message}
                            </p>
                            {notif.content && <p className="mt-1 pr-5 text-xs leading-relaxed text-white/45 whitespace-pre-wrap">{notif.content}</p>}
                            <p className="text-xs text-white/40 mt-1">{notif.time}</p>
                            {notif.offerId && (
                              <div className="mt-2.5 flex gap-2">
                                <button onClick={() => openAcceptOffer(notif)} className="inline-flex items-center rounded-lg bg-green-500/15 px-3 py-1.5 text-xs font-medium text-green-300 transition hover:bg-green-500/25 hover:text-green-200">Accepter l'offre</button>
                                <button onClick={() => declineOffer(notif)} className="inline-flex items-center rounded-lg bg-white/5 px-3 py-1.5 text-xs font-medium text-white/50 transition hover:bg-white/10 hover:text-white/70">Refuser</button>
                              </div>
                            )}
                          </div>
                        ))}
                        {notifications.length === 0 && (
                          <div className="text-center py-8 text-white/40">
                            <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>{t('nav_no_notifications')}</p>
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button data-testid="user-menu-btn" variant="ghost" className="flex items-center gap-2 text-white/60 hover:text-white">
                        <UserAvatar profile={profile} size={32} />
                        <span className="text-sm hidden sm:block">{profile?.username || user.username}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60 rounded-xl border-white/10 bg-[#0f1019]/95 p-1.5 backdrop-blur-xl">
                      <div className="flex items-center gap-3 px-2 py-2.5">
                        <UserAvatar profile={profile} size={36} />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-white">{profile?.username || user.username}</p>
                          <p className="truncate text-xs text-white/40">{profile?.email || user.email}</p>
                        </div>
                      </div>
                      <DropdownMenuSeparator className="bg-white/5" />
                      <DropdownMenuItem data-testid="dashboard-menu-item" onClick={() => navigate('/dashboard')} className="cursor-pointer rounded-lg py-2 focus:bg-white/[0.06]"><LayoutDashboard className="w-4 h-4 mr-2" />{t('nav_dashboard')}</DropdownMenuItem>
                      <DropdownMenuItem data-testid="settings-menu-item" onClick={() => navigate('/settings')} className="cursor-pointer rounded-lg py-2 focus:bg-white/[0.06]"><Settings className="w-4 h-4 mr-2" />{t('nav_settings')}</DropdownMenuItem>
                      {isAdmin && (
                        <DropdownMenuItem data-testid="admin-menu-item" onClick={() => navigate('/admin')} className="cursor-pointer rounded-lg py-2 focus:bg-white/[0.06] text-red-400"><Shield className="w-4 h-4 mr-2" />{t('nav_admin')}</DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator className="bg-white/5" />
                      <DropdownMenuItem data-testid="logout-menu-item" onClick={handleLogout} className="cursor-pointer rounded-lg py-2 text-red-400 focus:bg-red-500/10"><LogOut className="w-4 h-4 mr-2" />{t('nav_logout')}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <Button data-testid="navbar-login-btn" onClick={handleAuthClick} className="auth-cta relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-5 text-sm font-medium text-white shadow-[0_8px_24px_-10px_rgba(79,70,229,0.9)] hover:brightness-110">{t('nav_login')}</Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} onAuthClick={handleAuthClick} />
      {!onAuthClick && (
        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          mode={authMode}
          setMode={setAuthMode}
        />
      )}

      <Dialog open={!!offerToAccept} onOpenChange={(open) => !open && setOfferToAccept(null)}>
        <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Gift className="w-4 h-4 text-green-400" />Accepter l'offre</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-white/60">{offerToAccept?.description}</p>
            {!offerToAccept?.downloadable && (<>
            <div className={`rounded-lg border p-3 ${offerNeedsDiscord ? "border-[#5865F2]/40 bg-[#5865F2]/10" : "border-green-500/30 bg-green-500/10"}`}>
              <p className="text-xs font-medium flex items-center gap-1.5">
                <MessageCircle className="w-3.5 h-3.5 text-[#8b95ff]" />
                {offerNeedsDiscord ? "1. Connecte ton compte Discord" : "Compte Discord connecté"}
              </p>
              {offerNeedsDiscord ? (<>
                <p className="text-xs text-white/50 mt-1">Le bot est relié à ton compte Discord : c'est lui qui en sera propriétaire. Une fois connecté, reviens accepter l'offre depuis la cloche.</p>
                <Button type="button" onClick={connectDiscordForOffer} className="mt-2 w-full bg-[#5865F2] hover:bg-[#4752c4] rounded-lg h-9 text-sm">
                  Connecter Discord
                </Button>
              </>) : (
                <p className="text-xs text-white/50 mt-1">Tu seras propriétaire du bot sur Discord.</p>
              )}
            </div>
            <div className={offerNeedsDiscord ? "opacity-40 pointer-events-none" : ""}>
              <Label className="text-white/60 text-xs">{offerNeedsDiscord ? "2. " : ""}Nom du bot</Label>
              <Input
                value={offerBotName}
                onChange={(e) => setOfferBotName(e.target.value)}
                maxLength={32}
                placeholder="Choisis un nom pour ton bot"
                className="bg-black/30 border-white/10 rounded-lg mt-1"
              />
            </div>
            <div className={offerNeedsDiscord ? "opacity-40 pointer-events-none" : ""}>
              <Label className="text-white/60 text-xs">{offerNeedsDiscord ? "3. " : ""}Token Discord de ton bot</Label>
              <Input
                type="password"
                autoComplete="off"
                value={offerToken}
                onChange={(e) => setOfferToken(e.target.value)}
                placeholder="Colle ici le token de ton application Discord"
                className="bg-black/30 border-white/10 rounded-lg mt-1 font-mono"
              />
              <p className="text-xs text-white/30 mt-1.5">Discord Developer Portal &gt; ton application &gt; Bot &gt; Reset Token. Le token est vérifié auprès de Discord.</p>
            </div>
            </>)}
            <Button onClick={confirmAcceptOffer} disabled={offerLoading || offerNeedsDiscord} className="w-full bg-green-600 hover:bg-green-700 rounded-lg">
              {offerLoading ? "Activation..." : offerToAccept?.downloadable ? "Recevoir le produit" : "Activer le bot"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Navbar;
